import { Component, FC, ReactNode, useMemo } from 'react'
import * as isNumber from 'lodash/isNumber'
import * as numeral from 'numeral'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import Table2, { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import { AccountTyped, DetailedAccount } from 'interfaces/accounts'
import { NOT_APPLICABLE } from 'constants/accounts'
import AbsentValue from 'components/atoms/AbsentValue'
import AccountBadge, { AccountBadgeProps } from 'components/features/pfm/AccountBadge/AccountBadge'
import AccountTooltip from 'components/features/pfm/AccountTooltip/AccountTooltip'
import withContext from 'hocs/withContext/withContext'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { disabledAccountsUidsSetSelector } from 'components/features/pageFilters/selectors'

type AccountsDetailsRowProps = AccountTyped & {
  table: TableContextType
  row: DetailedAccount
}
type MaybeAbsentCellPredicate<TEntity> = (row: TEntity) => boolean
type MaybeAbsentCellValidator<TEntity> = {
  copy: ReactNode
  predicate: MaybeAbsentCellPredicate<TEntity>
}
type MaybeAbsentCellProps<TEntity> = {
  row: TEntity
  validators?: MaybeAbsentCellValidator<TEntity>[]
}

// TODO rewrite to HOC over Table2.Cell
class MaybeAbsentCell<TEntity> extends Component<MaybeAbsentCellProps<TEntity>> {
  render () {
    const { row, validators, children } = this.props

    for (const validator of (validators || [])) {
      if (validator.predicate(row)) {
        return (
          <AbsentValue>{ validator.copy }</AbsentValue>
        )
      }
    }

    return <>{ children }</>
  }
}

const isManualValidator: MaybeAbsentCellValidator<DetailedAccount> = {
  copy: NOT_APPLICABLE,
  predicate: (row) => row.is_manual,
}
const isUnlinkedValidator: MaybeAbsentCellValidator<DetailedAccount> = {
  copy: 'Unlinked',
  predicate: (row) => row.link_status === 'unlinked',
}
const isRateUnknownValidator: MaybeAbsentCellValidator<DetailedAccount> = {
  copy: NOT_APPLICABLE,
  predicate: (row) => !isNumber(row.rate),
}
const isDueDateUnknownValidator: MaybeAbsentCellValidator<DetailedAccount> = {
  copy: NOT_APPLICABLE,
  predicate: (row) => !row.due_date,
}

const AccountsDetailsRow: FC<AccountsDetailsRowProps> = (props) => {
  const { row, type } = props
  const { category, amount, rate, minimum_amount_due, due_date } = row
  const rowKey = props.table.getRowKey(row)

  const disabledAccountsUidsSet = useStoreSelector(disabledAccountsUidsSetSelector)
  const validators: Record<string, MaybeAbsentCellValidator<DetailedAccount>[]> = useMemo(() => {
    const isFilteredValidator: MaybeAbsentCellValidator<DetailedAccount> = {
      copy: 'Filtered, not shown',
      predicate: (currentRow) => disabledAccountsUidsSet.has(currentRow.uid),
    }

    return {
      amount: [isFilteredValidator, isUnlinkedValidator],
      rate: [isFilteredValidator, isUnlinkedValidator, isManualValidator, isRateUnknownValidator],
      due: [isFilteredValidator, isUnlinkedValidator, isManualValidator],
      due_date: [isFilteredValidator, isUnlinkedValidator, isManualValidator, isDueDateUnknownValidator],
    }
  }, [disabledAccountsUidsSet])

  const isDebts = type === 'debts'

  const accountBadgeProps: AccountBadgeProps = {
    name: row.name,
    cardNumber: row.number,
    cardType: row.card_type,
    updatedAt: row.updated_at,
    tooltipChild: (
      <AccountTooltip type={ type } account={ row } />
    ),
  }

  return (
    <Table2.Row key={ rowKey }>

      <Table2.Cell rowKey={ rowKey } name='account_type'>
        { category.value }
      </Table2.Cell>

      <Table2.Cell rowKey={ rowKey } name='account_name'>
        <AccountBadge { ...accountBadgeProps } />
      </Table2.Cell>

      {
        isDebts && (
          <Table2.Cell rowKey={ rowKey } name='rate'>
            <MaybeAbsentCell validators={ validators['rate'] } row={ row }>
              { isNumber(rate) && numeral(rate / 100).format('0.00%') }
            </MaybeAbsentCell>
          </Table2.Cell>
        )
      }

      <Table2.Cell rowKey={ rowKey } name='amount'>
        <MaybeAbsentCell validators={ validators['amount'] } row={ row }>
          { formatTableCurrency(amount) }
        </MaybeAbsentCell>
      </Table2.Cell>

      {
        isDebts && (
          <Table2.Cell rowKey={ rowKey } name='due'>
            <MaybeAbsentCell validators={ validators['due'] } row={ row }>
              { formatTableCurrency(minimum_amount_due) }
            </MaybeAbsentCell>
          </Table2.Cell>
        )
      }

      {
        isDebts && (
          <Table2.Cell rowKey={ rowKey } name='due_date'>
            <MaybeAbsentCell validators={ validators['due_date'] } row={ row }>
              { due_date && formatUSDate(due_date) }
            </MaybeAbsentCell>
          </Table2.Cell>
        )
      }

    </Table2.Row>
  )
}

export default withContext(TableContext, 'table')(AccountsDetailsRow)
